import React, { useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { BookOpen } from "lucide-react";
import { getContinueReading, isEpisodeComplete } from "@/lib/storyProgress";
import { getEpisode, resolveLevel, SERIES } from "@/data/stories";
import { STORY_ARCS } from "@/data/storyArcs";
import { useTierPresentation } from "@/lib/ageCopy";
import { track } from "@/lib/track";

/** Reads existing story progress only. Renders when an episode is part-read (not finished); collapses otherwise. */
export function useStoryContinue() {
  return useMemo(() => {
    const cur = getContinueReading();
    if (!cur?.seriesSlug || !cur?.episodeSlug) return null;
    if (isEpisodeComplete(cur.seriesSlug, cur.episodeSlug)) return null;
    const ep = getEpisode(cur.seriesSlug, cur.episodeSlug);
    const series = SERIES.find((s) => s.slug === cur.seriesSlug);
    if (!ep || !series) return null;
    const pages = resolveLevel(ep, cur.band)?.level?.pages?.length || 0;
    const pageIndex = Math.max(0, Number(cur.pageIndex) || 0);
    if (pageIndex < 1) return null; // cover only — not "half-read"
    const arc = STORY_ARCS.find((a) => a.title === series.title);
    const arcEp = arc?.episodes?.[ep.index - 1];
    return { series, ep, pageIndex, pages, hook: arcEp?.cliffhanger || arcEp?.narrativeIntro || series.blurb, to: `/story/${series.slug}/${ep.slug}` };
  }, []);
}

export default function StoryContinueCard() {
  const s = useStoryContinue();
  const { tier, emoji } = useTierPresentation();
  useEffect(() => { if (s) track("story_continue_shown", { series: s.series.slug }); }, [s]);
  if (!s) return null;
  const pct = s.pages ? Math.min(100, Math.round((s.pageIndex / (s.pages + 1)) * 100)) : 0;
  return (
    <section className="mt-3 mwq-card mwq-card-tint-creative" data-testid="today-story-continue" aria-labelledby="today-story-title">
      <div className="flex items-start gap-3">
        <span className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-white text-creative-safe" aria-hidden>
          {emoji ? <span className="text-2xl">{s.series.emoji}</span> : <BookOpen size={22} strokeWidth={2.5} />}
        </span>
        <div className="min-w-0 flex-1">
          <p className="mwq-meta text-creative-safe">{tier === "pioneers" ? "Story in progress" : "Your story is waiting"}</p>
          <h2 id="today-story-title" className="mwq-card-title">{s.series.title} · {s.ep.title}</h2>
          {s.hook && <p className="mwq-body text-[15px] mt-1 italic" data-testid="today-story-hook">“{s.hook}”</p>}
          <p className="mwq-meta mt-1" data-testid="today-story-progress">Page {s.pageIndex} of {s.pages}{pct ? ` · ${pct}% read` : ""}</p>
          <Link to={s.to} data-testid="today-story-continue-cta" onClick={() => track("story_continue_clicked", { series: s.series.slug })} className="mwq-btn mwq-btn-secondary mt-3 min-h-[44px]">
            Continue the story →
          </Link>
        </div>
      </div>
    </section>
  );
}
